import { PublicClientApplication } from '@azure/msal-browser';
import { sharePointConfig } from '../config/sharepoint';

/** Raised when no Graph token can be had without the user acting (first use, expired consent). */
export class GraphAuthRequiredError extends Error {
  constructor() {
    super('Sign in to Microsoft 365 to continue.');
    this.name = 'GraphAuthRequiredError';
  }
}

let client: Promise<PublicClientApplication> | undefined;

function getClient(): Promise<PublicClientApplication> {
  client ??= (async () => {
    const instance = new PublicClientApplication({
      auth: {
        clientId: sharePointConfig.clientId,
        authority: sharePointConfig.authority,
        redirectUri: `${window.location.origin}${sharePointConfig.redirectPath}`
      },
      cache: { cacheLocation: 'sessionStorage' }
    });
    await instance.initialize();
    return instance;
  })();
  return client;
}

/** The SWA sign-in email, so Entra can pick the same account without asking. */
export async function acquireGraphTokenSilently(loginHint?: string): Promise<string | null> {
  const msal = await getClient();
  const request = { scopes: sharePointConfig.scopes };
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];

  try {
    if (account) {
      return (await msal.acquireTokenSilent({ ...request, account })).accessToken;
    }

    if (loginHint) {
      const result = await msal.ssoSilent({ ...request, loginHint });
      msal.setActiveAccount(result.account);
      return result.accessToken;
    }
  } catch {
    // Needs the user, a blocked iframe or no session: all fall back to the connect popup.
  }

  return null;
}

/** Must run from a click handler, or the browser blocks the popup. */
export async function connectGraph(loginHint?: string): Promise<string> {
  const msal = await getClient();
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
  const result = await msal.acquireTokenPopup({
    scopes: sharePointConfig.scopes,
    loginHint: account?.username ?? loginHint
  });
  msal.setActiveAccount(result.account);
  return result.accessToken;
}

export async function getGraphToken(): Promise<string> {
  const token = await acquireGraphTokenSilently();
  if (!token) throw new GraphAuthRequiredError();
  return token;
}
